import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
  TableSortLabel,
  Box,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
  GetEquipments,
  DeleteEquipment,
} from "../../../services/GeneralMaintenanceService";

import CommonLoader from "../../common/CommonLoader";
import ViewEquipment from "./ViewEquipment";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import { NotificationManager } from "react-notifications";
import AddEditEquipmentDialog from "./AddEditEquipmentDialog";

import { getComparator, stableSort } from "../../../../common-utilities/sortingUtils";

const EquipmentsTable = () => {
  const columns = [
    "Item ID",
    "Item",
    "Asset ID",
    "Description",
    "Make",
    "Model",
    "Serial No",
    "Equipment Type",
    "Actions",
  ];

  const columnHeaders = [
    "itemId",
    "item",
    "assetId",
    "description",
    "make",
    "model",
    "serialNumber",
    "equipmentType",
  ];

  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [equipmentsData, setEquipmentsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");

  // FIXED: separate states
  const [viewEquipment, setViewEquipment] = useState(null);
  const [editEquipment, setEditEquipment] = useState(null);

  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [selectedItemToDelete, setSelectedItemToDelete] = useState(null);

  const [isAddEditDialogOpen, setIsAddEditDialogOpen] = useState(false);

  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");

  const tableContainerRef = useRef(null);
  const [tableHeight, setTableHeight] = useState(600);

  useEffect(() => {
    const fetchEquipments = async () => {
      try {
        setLoading(true);
        const response = await GetEquipments();
        setEquipmentsData(response.results);
        setFilteredData(response.results);
      } finally {
        setLoading(false);
      }
    };

    fetchEquipments();
  }, []);

  useEffect(() => {
    const filtered = equipmentsData.filter((e) =>
      columnHeaders.some((h) =>
        e[h]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );

    setFilteredData(filtered);
  }, [searchTerm, equipmentsData]);

  useEffect(() => {
    const calc = () => {
      if (!tableContainerRef.current) return;

      const top = tableContainerRef.current.getBoundingClientRect().top;
      const h = window.innerHeight;

      const available = h - top - 80;

      setTableHeight(Math.max(500, Math.min(available, h - 100)));
    };

    calc();
    window.addEventListener("resize", calc);

    return () => window.removeEventListener("resize", calc);
  }, [filteredData]);

  const handleRequestSort = (prop) => {
    const isAsc = orderBy === prop && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(prop);
  };

  const sortedData = orderBy
    ? stableSort(filteredData, getComparator(order, orderBy))
    : filteredData;

  const renderActions = (equipment) => (
    <div style={{ minWidth: "120px" }}>
      {/* EDIT → OPEN DIALOG */}
      <IconButton
        style={{ color: "orange" }}
        onClick={() => {
          setEditEquipment(equipment);
          setIsAddEditDialogOpen(true);
        }}
      >
        <EditIcon />
      </IconButton>

      {/* VIEW → OPEN VIEW PANEL */}
      <IconButton
        style={{ color: "green" }}
        onClick={() => setViewEquipment(equipment)}
      >
        <VisibilityIcon />
      </IconButton>

      {/* DELETE */}
      <IconButton
        style={{ color: "red" }}
        onClick={() => {
          setSelectedItemToDelete(equipment);
          setIsDeleteConfirmationOpen(true);
        }}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  return (
    <>
      <span className="title">Equipments Inventory</span>
      <hr />

      {/* ================= VIEW MODE ================= */}
      {viewEquipment ? (
        <>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => setViewEquipment(null)}
            style={{ marginBottom: 16, color: "green" }}
          >
            Back
          </Button>

          <ViewEquipment equipment={viewEquipment} />
        </>
      ) : (
        <>
          {/* ================= TABLE ================= */}
          <div style={{ display: "flex", marginBottom: 16 }}>
            <TextField
              size="small"
              label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: "80%", marginRight: 16 }}
            />

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditEquipment(null);
                setIsAddEditDialogOpen(true);
              }}
            >
              New
            </Button>
          </div>

          <Paper>
            <Box sx={{ width: "99%", overflowX: "auto" }}>
              <TableContainer
                ref={tableContainerRef}
                sx={{
                  maxHeight: tableHeight,
                  overflowY: "auto",
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {columns.map((col, i) => (
                        <TableCell
                          key={col}
                          onClick={() =>
                            i < columnHeaders.length &&
                            handleRequestSort(columnHeaders[i])
                          }
                          style={{ cursor: "pointer" }}
                        >
                          {i < columnHeaders.length ? (
                            <TableSortLabel
                              active={orderBy === columnHeaders[i]}
                              direction={
                                orderBy === columnHeaders[i] ? order : "asc"
                              }
                            >
                              {col}
                            </TableSortLabel>
                          ) : (
                            col
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {sortedData
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((equipment, index) => (
                        <TableRow
                          key={equipment.itemId}
                          sx={{
                            backgroundColor:
                              index % 2 === 0
                                ? "rgba(0,0,0,0.04)"
                                : "white",
                          }}
                        >
                          {columnHeaders.map((col) => (
                            <TableCell key={col}>
                              {equipment[col]}
                            </TableCell>
                          ))}

                          <TableCell>{renderActions(equipment)}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>

          <CommonLoader loading={loading} />

          <DeleteConfirmation
            isOpen={isDeleteConfirmationOpen}
            onClose={() => setIsDeleteConfirmationOpen(false)}
            onConfirm={async () => {
              await DeleteEquipment(selectedItemToDelete.itemId);
              setIsDeleteConfirmationOpen(false);
            }}
            title="Delete Confirmation"
            message="Are you sure?"
            color="red"
          />

          {/* ================= EDIT DIALOG ================= */}
          {isAddEditDialogOpen && (
            <AddEditEquipmentDialog
              open={isAddEditDialogOpen}
              onClose={() => {
                setIsAddEditDialogOpen(false);
                setEditEquipment(null);
              }}
              selectedEquipment={editEquipment}
              onSave={async () => {
                setIsAddEditDialogOpen(false);
                setEditEquipment(null);
                setLoading(true);
                try {
                  const response = await GetEquipments();
                  setEquipmentsData(response.results);
                  setFilteredData(response.results);
                } finally {
                  setLoading(false);
                }
              }}
            />
          )}
        </>
      )}
    </>
  );
};

export default EquipmentsTable;